import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Ruler, MapPin, Footprints, ExternalLink, Sparkles } from "lucide-react";
import { OrnamentDivider, VineOrnament } from "@/components/Ornaments";
import RegionMap from "@/components/RegionMap";
import SmhiWidget from "@/components/SmhiWidget";
import { findTrailBySlug, SOURCE_URL, PDF_STIGBESKR } from "@/data/trails";
import { findRouteBySlug } from "@/data/trailRoutes";
import ShareWidget from "@/components/ShareWidget";
import AndringsforslagButton from "@/components/AndringsforslagButton";

function computeBounds(coords) {
  if (!coords || coords.length === 0) return null;
  let minLng = Infinity, maxLng = -Infinity, minLat = Infinity, maxLat = -Infinity;
  coords.forEach(([lng, lat]) => {
    if (lng < minLng) minLng = lng;
    if (lng > maxLng) maxLng = lng;
    if (lat < minLat) minLat = lat;
    if (lat > maxLat) maxLat = lat;
  });
  // Add small margin if the trail is a near-point (tiny bounds)
  const lngPad = Math.max(0.003, (maxLng - minLng) * 0.25);
  const latPad = Math.max(0.002, (maxLat - minLat) * 0.25);
  return [[minLng - lngPad, minLat - latPad], [maxLng + lngPad, maxLat + latPad]];
}

export default function FriluftslivLedDetalj() {
  const { slug } = useParams();
  const { t } = useTranslation();
  const trail = findTrailBySlug(slug);
  const route = findRouteBySlug(slug);

  useEffect(() => {
    if (trail) document.title = `${trail.namn} – Vandringsled – vidalv.live`;
    window.scrollTo(0, 0);
  }, [slug, trail]);

  if (!trail) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-14 text-center">
        <h1 className="font-heading text-3xl font-semibold text-ink mb-4">Leden finns inte</h1>
        <Link to="/friluftsliv" className="text-pine hover:text-pine-hover no-underline font-semibold">
          Tillbaka till Friluftsliv
        </Link>
      </div>
    );
  }

  const bounds = route ? computeBounds(route.coords) : null;
  const startMarker = route && route.coords.length > 0
    ? [{
        id: "start",
        lng: route.coords[0][0],
        lat: route.coords[0][1],
        cat: "Friluftsliv",
        hoverLabel: `Start: ${trail.startPoint || trail.namn}`,
      }]
    : [];

  // External links: Google Maps route to start point + OSM view
  const startLL = route && route.coords[0] ? `${route.coords[0][1]},${route.coords[0][0]}` : null;
  const gmapsUrl = startLL ? `https://www.google.com/maps/search/?api=1&query=${startLL}` : null;
  const osmUrl = bounds
    ? `https://www.openstreetmap.org/?bbox=${bounds[0][0]},${bounds[0][1]},${bounds[1][0]},${bounds[1][1]}&layers=C`
    : null;

  const hasRoute = Boolean(route);

  return (
    <div data-testid={`led-detalj-${slug}`} className="relative max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
      <VineOrnament className="hidden lg:block absolute top-10 -left-20 w-14 h-40 text-gold-deep opacity-30" />
      <VineOrnament className="hidden lg:block absolute top-10 -right-20 w-14 h-40 text-gold-deep opacity-30" flip />

      <Link to="/friluftsliv" className="inline-flex items-center gap-1.5 text-xs font-semibold text-bark hover:text-pine no-underline transition-colors mb-6">
        <ArrowLeft size={14} /> Tillbaka till Friluftsliv
      </Link>

      <div className="flex items-center gap-2 mb-1.5">
        <Footprints size={18} className="text-pine" />
        <p className="font-body text-[11px] font-semibold text-bark/55 uppercase tracking-[0.22em]">
          Vandringsled i {trail.area}
        </p>
      </div>
      <h1 className="font-fraktur-smooth text-xl sm:text-2xl text-falu tracking-wide mb-3 leading-[1.05]">
        {trail.namn}
      </h1>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <span className="inline-flex items-center gap-1.5 text-sm font-body font-semibold text-pine bg-pine/10 px-3 py-1 rounded-sm">
          <Ruler size={13} /> {trail.km} km
        </span>
        {trail.startPoint && (
          <span className="inline-flex items-center gap-1.5 text-sm font-body text-bark/70">
            <MapPin size={13} /> Start: {trail.startPoint}
          </span>
        )}
        {trail.status === "stängd" && (
          <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-falu bg-falu/10 px-2 py-1 rounded-sm">
            ⚠ Stigen är stängd
          </span>
        )}
      </div>

      <OrnamentDivider className="text-gold-deep opacity-40 my-3" />

      <p className="text-base text-bark mb-4 leading-relaxed">{trail.text}</p>

      <div className="mb-6">
        <ShareWidget
          title={`${trail.namn} – vandringsled`}
          text={trail.text}
          path={`/friluftsliv/led/${slug}`}
        />
      </div>

      {/* SMHI weather */}
      {startMarker.length > 0 && (
        <SmhiWidget
          lat={startMarker[0].lat}
          lng={startMarker[0].lng}
          placeName={trail.startPoint || trail.namn}
        />
      )}

      {trail.storyLink && (
        <Link to={trail.storyLink}
          data-testid="trail-story-link"
          className="inline-flex items-center gap-2 mb-6 text-sm font-body font-semibold text-angermanland-deep bg-angermanland/10 hover:bg-angermanland/15 border border-angermanland/30 px-3 py-2 rounded-sm no-underline transition-colors">
          <Sparkles size={13} /> Läs berättelsen om Mystiska korset →
        </Link>
      )}

      {/* Detail-karta */}
      <section className="mb-6">
        <h2 className="font-fraktur-smooth text-xl sm:text-2xl text-falu tracking-wide mb-2 leading-tight">Ledens sträckning</h2>

        {hasRoute && (
          <div data-testid="approx-notice" className="mb-3 flex gap-3 bg-gold/10 border-l-4 border-gold-deep rounded-sm p-3 sm:p-4">
            <span className="text-gold-deep text-lg leading-none mt-0.5">⚠</span>
            <div className="flex-1 text-xs sm:text-sm text-ink/80 leading-relaxed">
              <p className="font-semibold text-ink mb-1">Detta är en ungefärlig sträckning</p>
              <p className="mb-1">
                Kartan nedan är hand-digitiserad från backe.nu:s PDF-översikt (noggrannhet ±50–150 m). Den ger en översikt – men är <em>inte</em> lämplig att navigera efter i fält.
              </p>
              <p className="text-ink/70 text-xs">
                <strong>För din vandring rekommenderar vi:</strong> ladda ner den officiella <a href={PDF_STIGBESKR} target="_blank" rel="noopener noreferrer" className="text-pine hover:text-pine-hover font-semibold">PDF-kartan &amp; stigbeskrivningarna från backe.nu</a>, följ <strong>skyltning i terrängen</strong>, eller fråga på plats på <em>Backe Byservicekontor</em> (där finns tryckt karta gratis). Kontakta <em>Fjällsjö Framtid</em> för GPX-spår om du vill navigera med mobilen.
              </p>
            </div>
          </div>
        )}

        <div data-testid="led-karta-container" className="relative w-full h-[440px] rounded-sm overflow-hidden border border-warm-border bg-parchment">
          {hasRoute ? (
            <RegionMap
              variant="page"
              center={[route.coords[Math.floor(route.coords.length / 2)][0], route.coords[Math.floor(route.coords.length / 2)][1]]}
              zoom={13}
              markers={startMarker}
              lines={[{ ...route, color: route.color || "#3F6B4E" }]}
              fitBounds={bounds}
              className="absolute inset-0"
            />
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center text-bark/60 text-sm text-center p-6">
              <MapPin size={24} className="mb-2 text-bark/40" />
              <p className="mb-2">För den här leden finns ännu ingen ritad sträckning.</p>
              <p className="text-xs text-bark/50">Använd PDF-kartan nedan eller fråga Silsjönäs Bygdegård för handritad karta.</p>
            </div>
          )}
        </div>
        <p className="text-[11px] text-bark/55 italic mt-2">
          {hasRoute
            ? "Streckad linje = ungefärlig sträckning, hand-digitiserad från backe.nu-kartan (±50–150 m). För exakt sträckning, följ skyltning i terrängen."
            : "Exakt sträckning finns i tryckt karta och på plats."}
        </p>
      </section>

      {/* External links */}
      <section className="mb-8 flex flex-wrap gap-2">
        {gmapsUrl && (
          <a href={gmapsUrl} target="_blank" rel="noopener noreferrer"
            data-testid="link-gmaps"
            className="inline-flex items-center gap-1.5 text-xs font-body font-semibold text-parchment bg-pine hover:bg-pine-hover px-3 py-2 rounded-sm no-underline transition-colors">
            <MapPin size={13} /> Öppna startpunkt i Google Maps
          </a>
        )}
        {osmUrl && (
          <a href={osmUrl} target="_blank" rel="noopener noreferrer"
            data-testid="link-osm"
            className="inline-flex items-center gap-1.5 text-xs font-body font-semibold text-pine bg-parchment border border-pine/40 hover:bg-pine/5 px-3 py-2 rounded-sm no-underline transition-colors">
            <ExternalLink size={13} /> Öppna området i OpenStreetMap
          </a>
        )}
        <a href={SOURCE_URL} target="_blank" rel="noopener noreferrer"
          data-testid="link-source"
          className="inline-flex items-center gap-1.5 text-xs font-body font-semibold text-pine bg-parchment border border-pine/40 hover:bg-pine/5 px-3 py-2 rounded-sm no-underline transition-colors">
          <ExternalLink size={13} /> backe.nu – alla stigbeskrivningar
        </a>
      </section>

      {/* Footer note */}
      <p className="text-[11px] text-bark/55 italic border-t border-warm-border pt-4">
        Ledbeskrivning: <a href={SOURCE_URL} target="_blank" rel="noopener noreferrer" className="text-pine hover:text-pine-hover">backe.nu/vandringseldorado</a> (Fjällsjö Framtid). Routen är hand-digitiserad på vidalv.live från PDF-kartan.
      </p>
      <div className="mt-4 pt-3 border-t border-warm-border/60">
        <AndringsforslagButton typ="led" referensId={slug} referensNamn={trail?.name || slug} />
      </div>
    </div>
  );
}
